import { useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import api from '../api/client'
import { scadaApi, type AlertEvent } from '../api/scada'
import { useAuthStore } from '../store/authStore'
import { Badge, severityTone } from '../components/Badge'

// ── API calls ─────────────────────────────────────────────────────────────────

interface MonthlyRow {
  month: string          // 'YYYY-MM'
  reading_count: number
  total_value: number
}

const reportsApi = {
  consumption: (months: number) =>
    api.get<MonthlyRow[]>('/reports/consumption', { params: { months } }),
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

function monthsAgo(n: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() - n)
  return d.toISOString().slice(0, 10)
}

function fmt(n: number): string {
  return n.toLocaleString('sr-RS', { maximumFractionDigits: 0 })
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-xs p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}

// ── CSV export section ────────────────────────────────────────────────────────

function CsvExport() {
  const { token } = useAuthStore()
  const [fromDate, setFromDate] = useState(monthsAgo(1))
  const [toDate, setToDate]     = useState(today())
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const linkRef = useRef<HTMLAnchorElement>(null)

  async function handleExport() {
    setError(null)
    setLoading(true)
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate })
      const res = await fetch(`/api/v1/reports/readings-export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setError(body?.detail ?? `HTTP ${res.status}`)
        return
      }
      const blob = await res.blob()
      const url  = URL.createObjectURL(blob)
      const a    = linkRef.current!
      a.href     = url
      a.download = `ocitavanja_${fromDate}_${toDate}.csv`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    } catch {
      setError('Greška pri preuzimanju')
    } finally {
      setLoading(false)
    }
  }

  const rangeDays =
    fromDate && toDate
      ? Math.round((new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86_400_000)
      : 0

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          Izvoz očitavanja (CSV)
        </h2>
      </div>
      <div className="p-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Od datuma</label>
            <input
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Do datuma</label>
            <input
              type="date"
              value={toDate}
              min={fromDate}
              max={today()}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={handleExport}
            disabled={loading || !fromDate || !toDate || rangeDays <= 0 || rangeDays > 366}
            className="px-5 py-2 text-sm font-medium rounded-lg bg-brand-cyan text-brand-midnight hover:bg-brand-cyan-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Preuzimanje…
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Preuzmi CSV
              </>
            )}
          </button>
        </div>

        {rangeDays > 366 && (
          <p className="mt-2 text-xs text-red-500">Maksimalni opseg je 366 dana.</p>
        )}
        {rangeDays > 0 && rangeDays <= 366 && (
          <p className="mt-2 text-xs text-gray-400">
            Opseg: {rangeDays} {rangeDays === 1 ? 'dan' : 'dana'}
          </p>
        )}
        {error && (
          <p className="mt-2 text-xs text-red-500">{error}</p>
        )}
        {/* Hidden anchor for programmatic click */}
        <a ref={linkRef} className="hidden" />
      </div>
    </div>
  )
}

// ── Consumption chart ─────────────────────────────────────────────────────────

function ConsumptionChart() {
  const [months, setMonths] = useState(12)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['consumption', months],
    queryFn: () => reportsApi.consumption(months).then((r) => r.data),
  })

  const total   = data?.reduce((s, r) => s + r.total_value, 0) ?? 0
  const count   = data?.reduce((s, r) => s + r.reading_count, 0) ?? 0
  const avgMonth = data?.length ? total / data.length : 0

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <StatCard
          label="Ukupna potrošnja"
          value={`${fmt(total)} m³`}
          sub={`Zadnjih ${months} meseci`}
        />
        <StatCard
          label="Prosek / mesec"
          value={`${fmt(avgMonth)} m³`}
        />
        <StatCard
          label="Broj očitavanja"
          value={fmt(count)}
          sub="U izabranom periodu"
        />
      </div>

      {/* Chart */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Mesečna potrošnja (m³)
          </h2>
          <div className="flex items-center gap-1">
            {[6, 12, 24].map((m) => (
              <button
                key={m}
                onClick={() => setMonths(m)}
                className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                  months === m
                    ? 'bg-brand-cyan text-brand-midnight'
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                {m}M
              </button>
            ))}
          </div>
        </div>

        <div className="p-4">
          {isLoading && (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              Učitavanje…
            </div>
          )}
          {isError && (
            <div className="h-64 flex items-center justify-center text-red-400 text-sm">
              Greška pri učitavanju podataka
            </div>
          )}
          {data && data.length === 0 && (
            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
              Nema podataka za izabrani period
            </div>
          )}
          {data && data.length > 0 && (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={data.map((r) => ({
                  month: r.month,
                  'Potrošnja (m³)': Math.round(r.total_value),
                  'Očitavanja': r.reading_count,
                }))}
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#6b7280' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                />
                <Bar dataKey="Potrošnja (m³)" fill="var(--color-brand-cyan)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  )
}

// ── SCADA alert history ──────────────────────────────────────────────────────

// Row text (title, label) comes from the backend -- the same text the push
// carried. This map exists only to label the *type filter dropdown*, which
// has no row to read a title from; a GET /scada/alert-types catalog would
// remove it.
const ALERT_TYPE_FILTER_LABEL: Record<string, string> = {
  pump_offline:            'Pumpa stoji',
  pump_fault:              'Kvar pumpe',
  device_offline:          'Nema signala',
  edge_offline:            'Edge server van mreže',
  low_reservoir:           'Nizak nivo rezervoara',
  low_time_to_empty:       'Kratko vreme do praznog',
  pump_failed_to_start:    'Pumpa nije pokrenuta',
  sensor_divergence:       'Odstupanje senzora',
  device_threshold_breach: 'Vrednost van opsega',
  zone_flow_imbalance:     'Neravnoteža protoka u zoni',
  recovered:               'Oporavak',
  // Detection alert types (device-ownership step 3, migration 056) --
  // label only; pipe_meter_gap's device_key is "pipe:<link id>", not a
  // real device, so it still falls back to the raw key in the Uređaj
  // column until that's resolved to the pipe's own label (not done here).
  pipe_meter_gap:          'Razlika protoka na cevovodu',
  pressure_flow_anomaly:   'Anomalija pritiska',
}

function alertTypeFilterLabel(t: string): string {
  return ALERT_TYPE_FILTER_LABEL[t] ?? t.replace(/_/g, ' ')
}

// Episode state: open until resolved_at is set (a cleared-but-not-yet-
// resolved episode is still inside its 10-minute flap guard).
function episodeStatus(e: AlertEvent): string {
  if (e.alert_type === 'recovered') return 'Oporavak'
  if (e.resolved_at) return `Rešen ${new Date(e.resolved_at).toLocaleString('sr-Latn-RS')}`
  return 'Otvoren'
}

function csvField(v: string): string {
  return `"${v.replace(/"/g, '""')}"`
}

// Client-side export of the currently filtered/loaded page — there's no
// server-side streaming export for scada_alert_events (unlike readings-
// export above), and the alert-events endpoint caps at 500 rows, so this
// covers "export what you're looking at" rather than an arbitrary range.
function exportAlertEventsCsv(events: AlertEvent[], deviceLabel: (key: string) => string) {
  const header = ['Vreme', 'Uređaj', 'Ozbiljnost', 'Alarm', 'Status', 'Push'].map(csvField).join(',')
  const rows = events.map((e) => [
    new Date(e.fired_at).toLocaleString('sr-Latn-RS'),
    e.label ?? deviceLabel(e.device_key),
    e.severity ?? '',
    e.title,
    episodeStatus(e),
    e.notified,
  ].map((f) => csvField(String(f))).join(','))
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url  = URL.createObjectURL(blob)
  const a    = document.createElement('a')
  a.href = url
  a.download = `scada-alarmi_${today()}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function useAlertHistory() {
  const [deviceKey, setDeviceKey] = useState('')
  const [alertType, setAlertType] = useState('')
  const [since, setSince]         = useState(monthsAgo(1))
  const [until, setUntil]         = useState(today())

  const { data: devices = [] } = useQuery({
    queryKey: ['scada-devices-for-alert-history'],
    queryFn: () => scadaApi.listDevices().then((r) => r.data),
    staleTime: 60_000,
  })

  const { data: rawEvents = [], isLoading, isError } = useQuery({
    queryKey: ['scada-alert-events', deviceKey, alertType, since, until],
    queryFn: () => scadaApi.listAlertEvents({
      device_key: deviceKey || undefined,
      alert_type: alertType || undefined,
      since: since ? new Date(since).toISOString() : undefined,
      until: until ? new Date(`${until}T23:59:59`).toISOString() : undefined,
      limit: 200,
    }).then((r) => r.data),
  })

  const deviceLabel = (key: string) => devices.find((d) => d.device_key === key)?.label ?? key
  // Simulated (TEST button) rows are real rows on purpose, but not history.
  const events = rawEvents.filter((e) => e.detail.simulated !== true)

  // Frequency stats are computed from this same filtered/paginated fetch
  // (max 200 of a possible 500) — a sample of the current filter, not a
  // full aggregate query, called out as such in the UI.
  const countBy = (keyOf: (e: AlertEvent) => string) => {
    const counts: Record<string, number> = {}
    events.forEach((e) => { const k = keyOf(e); counts[k] = (counts[k] ?? 0) + 1 })
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 5)
  }

  return {
    devices, events, isLoading, isError, deviceLabel,
    topDevices: countBy((e) => e.label ?? deviceLabel(e.device_key)), topTypes: countBy((e) => e.alert_type),
    deviceKey, setDeviceKey, alertType, setAlertType, since, setSince, until, setUntil,
  }
}

export function AlertHistorySection() {
  const {
    devices, events, isLoading, isError, deviceLabel, topDevices, topTypes,
    deviceKey, setDeviceKey, alertType, setAlertType, since, setSince, until, setUntil,
  } = useAlertHistory()

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Istorija SCADA alarma</h2>
          <button onClick={() => exportAlertEventsCsv(events, deviceLabel)} disabled={events.length === 0}
            className="text-xs font-medium text-brand-cyan-text hover:text-brand-midnight disabled:opacity-40 disabled:cursor-not-allowed">
            Izvezi CSV ({events.length})
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Uređaj</label>
              <select value={deviceKey} onChange={(e) => setDeviceKey(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500">
                <option value="">Svi uređaji</option>
                {devices.map((d) => <option key={d.device_key} value={d.device_key}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tip alarma</label>
              <select value={alertType} onChange={(e) => setAlertType(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500">
                <option value="">Svi tipovi</option>
                {Object.entries(ALERT_TYPE_FILTER_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Od datuma</label>
              <input type="date" value={since} max={until} onChange={(e) => setSince(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Do datuma</label>
              <input type="date" value={until} min={since} max={today()} onChange={(e) => setUntil(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          {isLoading ? (
            <div className="h-32 flex items-center justify-center text-gray-400 text-sm">Učitavanje…</div>
          ) : isError ? (
            <div className="h-32 flex items-center justify-center text-red-400 text-sm">Greška pri učitavanju</div>
          ) : events.length === 0 ? (
            <div className="h-32 flex items-center justify-center text-gray-400 text-sm">Nema alarma za izabrani filter</div>
          ) : (
            <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gray-100 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vreme</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Uređaj</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Alarm</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Push</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {events.map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{new Date(e.fired_at).toLocaleString('sr-Latn-RS')}</td>
                      <td className="px-4 py-2 font-medium text-gray-900">{e.label ?? deviceLabel(e.device_key)}</td>
                      <td className="px-4 py-2">
                        <Badge tone={severityTone(e.severity)} title={alertTypeFilterLabel(e.alert_type)}>{e.title}</Badge>
                      </td>
                      <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap">{episodeStatus(e)}</td>
                      <td className="px-4 py-2 text-gray-500 text-xs">{e.notified}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {events.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xs p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Najčešći uređaji (u prikazanom uzorku)</h3>
            <ul className="space-y-1.5 text-sm">
              {topDevices.map(([key, count]) => (
                <li key={key} className="flex items-center justify-between">
                  <span className="text-gray-700">{deviceLabel(key)}</span>
                  <span className="text-gray-400 font-medium">{count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-xs p-5">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Najčešći tipovi alarma (u prikazanom uzorku)</h3>
            <ul className="space-y-1.5 text-sm">
              {topTypes.map(([type, count]) => (
                <li key={type} className="flex items-center justify-between">
                  <span className="text-gray-700">{alertTypeFilterLabel(type)}</span>
                  <span className="text-gray-400 font-medium">{count}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const { user } = useAuthStore()
  const hasMeterReading = user?.modules?.includes('meter_reading') ?? false
  const hasScada        = user?.modules?.includes('scada') ?? false

  return (
    <div className="p-6 space-y-8">
      {hasScada && (
        <section className="space-y-6">
          {hasMeterReading && <h2 className="text-lg font-bold text-gray-900">SCADA</h2>}
          <AlertHistorySection />
        </section>
      )}

      {hasMeterReading && (
        <section className="space-y-6">
          {hasScada && <h2 className="text-lg font-bold text-gray-900">Vodomeri</h2>}
          <CsvExport />
          <ConsumptionChart />
        </section>
      )}

      {!hasMeterReading && !hasScada && (
        <div className="text-center text-gray-400 text-sm py-12">Nema dostupnih modula za izveštaje.</div>
      )}
    </div>
  )
}
